import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { getSortOptionFromURI } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryRequestedPage } from "types/generated";

import {
  SCANS_INITIAL_SORT_DIRECTION,
  SCANS_INITIAL_SORT_OPTION,
  SCANS_LIMIT,
  SCANS_LIMIT_STORAGE_KEY,
} from "../components/Table/constants";
import { SEARCH_SCANS, SEARCH_SCANS_COUNT } from "./gql";

type UseScansProps = {
  skip: boolean;
};

const useScans = ({ skip }: UseScansProps) => {
  const { onError } = useTypedContext(FlashContext);
  const [searchParams] = useSearchParams();
  const [storedRowsPerPage] = useLocalStorage<number>(SCANS_LIMIT_STORAGE_KEY);

  const fullTextSearch = getSearchQuery(searchParams);

  const orderBy = useMemo(
    () =>
      getSortOptionFromURI(searchParams, SCANS_INITIAL_SORT_OPTION, SCANS_INITIAL_SORT_DIRECTION),
    [searchParams]
  );

  const initialVariables = useMemo(
    () => ({
      input: {
        first: storedRowsPerPage || SCANS_LIMIT,
        after: null,
        fullTextSearch,
        predicates: [],
        orderBy,
        requestedPage: null,
      },
    }),
    [storedRowsPerPage, fullTextSearch, orderBy]
  );

  const { error, loading, data, previousData, networkStatus, refetch } = usePolledQuery(
    SEARCH_SCANS,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
      skip,
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(SEARCH_SCANS_COUNT, {
    variables: { input: { fields: [], fullTextSearch, predicates: [] } },
    pollingGroup: PollingIntervalGroups.Lists,
    nextFetchPolicy: "cache-first",
    skip,
  });

  const loadScans = useCallback(
    async ({
      cursor,
      rowsPerPage,
      requestedPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      try {
        await refetch({
          input: {
            ...initialVariables.input,
            after: cursor,
            first: rowsPerPage,
            requestedPage,
          },
        });
      } catch (refetchError) {
        onError(refetchError);
      }
    },
    [onError, initialVariables, refetch]
  );

  const searchScans = data?.searchScans || previousData?.searchScans;

  const scans = useMemo(
    () => searchScans?.edges.map((edge) => edge.node) || [],
    [searchScans?.edges]
  );

  const totalCount =
    countData?.searchScansSuggestions.filteredCount ??
    previousCountData?.searchScansSuggestions.filteredCount ??
    0;

  const hasRows = scans.length > 0;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const paginationResetKey = useMemo(
    () => ({ fullTextSearch, orderBy }),
    [fullTextSearch, orderBy]
  );

  return {
    scans,
    error,
    loadingContent,
    hasRows,
    hasNoResults: !!searchScans && !hasRows && fullTextSearch.length > 0 && !loadingContent,
    loadScans,
    endCursor: searchScans?.pageInfo.endCursor,
    startCursor: searchScans?.pageInfo.startCursor,
    totalCount,
    paginationResetKey,
  };
};

export default useScans;
